/**
 * The bookkeeping half of sync: which records have unpushed local edits, how
 * far the pull cursor has got, and which account all of that belongs to.
 *
 * Split out from `sync.ts` so that `study.ts` can flag an edit without
 * importing the network layer — `sync.ts` needs `study.ts` (to re-seed after a
 * pull), and the reverse import would be a cycle.
 *
 * Everything lives in one localStorage object rather than several keys,
 * because the parts must move together: adopting a different account has to
 * clear the cursor and the dirty flags in the same breath, or the new account
 * inherits the old one's records.
 */

import { k, readJSON, writeJSON } from "./storage";
import { metaKey, monotonicNow, stampDirty, type MetaMap, type SyncKind } from "./merge";

export interface SyncState {
  /** Account these flags belong to; `null` while signed out. */
  userId: string | null;
  /** Highest server `rev` this device has pulled. */
  cursor: number;
  meta: MetaMap;
  /** High-water mark for this device's clock — see `monotonicNow`. */
  lastAt: number;
}

const KEY = k("sync.state.v1");
const DEVICE_KEY = k("device.v1");

const EMPTY: SyncState = { userId: null, cursor: 0, meta: {}, lastAt: 0 };

let state: SyncState = { ...EMPTY, ...readJSON<Partial<SyncState>>(KEY, {}) };

function persist(): void {
  writeJSON(KEY, state);
}

export function getSyncState(): Readonly<SyncState> {
  return state;
}

/**
 * A stable per-browser identifier. Used to give each device its own activity
 * counter bucket, so two devices never write the same activity record and
 * their totals add up instead of overwriting each other.
 */
let deviceId: string | null = null;

export function getDeviceId(): string {
  // Memoised rather than re-read: `writeJSON` silently no-ops where storage is
  // unavailable (private mode, tests), and without this the id would be
  // regenerated on every call and every answer would land in its own bucket.
  if (deviceId != null) return deviceId;
  deviceId = readJSON<string | null>(DEVICE_KEY, null);
  if (deviceId == null) {
    deviceId = crypto.randomUUID();
    writeJSON(DEVICE_KEY, deviceId);
  }
  return deviceId;
}

/**
 * Notified whenever something is flagged, so `sync.ts` can debounce a push
 * without `study.ts` having to import the network layer.
 */
let onDirty: (() => void) | null = null;

export function setDirtyListener(fn: (() => void) | null): void {
  onDirty = fn;
}

/**
 * True while a pull is being applied. Local writes made inside this window are
 * echoes of what the server already has, so flagging them would push them
 * straight back.
 */
let applyingRemote = false;

/**
 * Run `fn` without any of its writes being flagged for the next push.
 *
 * This is a structural guard rather than a convention. The alternative —
 * "remember to only call `markDirty` from the two study entry points" — breaks
 * the first time a new write path appears, and several already exist:
 * `replaceSrs`, `recordMastery` and `useMastery`'s setter are all exported and
 * callable. Anything that applies remote state or re-derives local state wraps
 * itself here instead.
 */
export function withRemoteApply<T>(fn: () => T): T {
  const previous = applyingRemote;
  applyingRemote = true;
  try {
    return fn();
  } finally {
    applyingRemote = previous;
  }
}

export function isApplyingRemote(): boolean {
  return applyingRemote;
}

/**
 * Flag one record as locally edited. Returns the timestamp recorded, or `null`
 * when the write was a remote apply and nothing was flagged.
 */
export function markDirty(kind: SyncKind, key: string, now = Date.now()): number | null {
  if (applyingRemote) return null;
  const at = monotonicNow(state.lastAt, now);
  state = {
    ...state,
    lastAt: at,
    meta: { ...state.meta, [metaKey(kind, key)]: { at, dirty: 1 } },
  };
  persist();
  onDirty?.();
  return at;
}

/**
 * Flag many records at once — adopting local progress into a fresh account, an
 * import, or a reset. All of them share one timestamp so the batch cannot tear.
 */
export function markManyDirty(
  entries: readonly { kind: SyncKind; key: string }[],
  now = Date.now()
): number | null {
  if (applyingRemote || entries.length === 0) return null;
  const at = monotonicNow(state.lastAt, now);
  state = {
    ...state,
    lastAt: at,
    meta: stampDirty(state.meta, entries.map((e) => metaKey(e.kind, e.key)), at),
  };
  persist();
  onDirty?.();
  return at;
}

/** Replace the metadata wholesale — used after a push or a pull settles. */
export function setMeta(meta: MetaMap): void {
  state = { ...state, meta };
  persist();
}

export function setCursor(cursor: number): void {
  state = { ...state, cursor };
  persist();
}

/**
 * Point the bookkeeping at an account, discarding everything the previous one
 * left behind.
 *
 * Called on sign-in and sign-out. Keeping a stale cursor across accounts would
 * make a second user's first pull return nothing — their account would look
 * empty — and keeping stale dirty flags would push the first user's records
 * into the second user's rows.
 */
export function adoptAccount(userId: string | null, cursor = 0, meta: MetaMap = {}): void {
  state = { ...state, userId, cursor, meta };
  persist();
}

/** Every key currently carrying metadata, dirty or not. */
export function knownMetaKeys(): string[] {
  return Object.keys(state.meta);
}
